import { useEffect, useRef, useState } from 'react';
import { callUIAPI } from '@utils/apiUtils';
import type { CustomerPersonaCard } from '../components/CustomersPanel';
import { personaCardsFromProgress } from '../utils/personaCards';

interface IntakeDraft {
  url: string;
  name: string;
  type: string;
  desc: string;
  specialties: string;
  location: string;
  companyTypeId?: number;
}

const DRAFT_KEY = 'onboarding.intakeDraft';

const MAX_RESUME_AGE_MS = 30 * 60 * 1000;

type OnboardingResume =
  | { status: 'loading' }
  | { status: 'none' }
  | {
      status: 'scan';
      intake: IntakeDraft;
      scanId: string;
      scanDbId: number;
      companyId: number | null;
      rescan: boolean;
      hasPrevious: boolean;
      completedAgents: string[];
      agentMessages: Record<string, string[]>;
      personas: CustomerPersonaCard[];
    };

export function saveIntakeDraft(draft: IntakeDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
  }
}

export function clearIntakeDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
  }
}

function readIntakeDraft(): Partial<IntakeDraft> {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') as Partial<IntakeDraft>;
  } catch {
    return {};
  }
}

interface LatestScanRecord {
  id?: number;
  companyId?: number | null;
  scanId?: string | null;
  status?: string;
  scanType?: string | null;
  hasPrevious?: boolean;
  companyName?: string;
  websiteUrl?: string;
  businessType?: string | null;
  createdOn?: string;
  progress?: { completedAgents?: string[]; messages?: Record<string, string[]>; personas?: unknown } | null;
}

export function useOnboardingResume(): OnboardingResume {
  const [state, setState] = useState<OnboardingResume>({ status: 'loading' });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let cancelled = false;
    const settle = (next: OnboardingResume) => {
      if (!cancelled) setState(next);
    };

    callUIAPI('scans/latest-pending', null, 'get', true)
      .then((res: Record<string, unknown>) => {
        const record = ((res as { data?: unknown })?.data ?? res) as LatestScanRecord | undefined;

        if (!record?.id) return settle({ status: 'none' });

        if (record.status !== 'pending' || !record.scanId) return settle({ status: 'none' });

        const startedAt = record.createdOn ? new Date(record.createdOn).getTime() : NaN;
        if (Number.isFinite(startedAt) && Date.now() - startedAt > MAX_RESUME_AGE_MS) {
          return settle({ status: 'none' });
        }

        const draft = readIntakeDraft();
        settle({
          status: 'scan',
          scanId: record.scanId,
          scanDbId: record.id,
          companyId: record.companyId ?? null,
          rescan: record.scanType === 'rescan',
          hasPrevious: !!record.hasPrevious,
          completedAgents: record.progress?.completedAgents ?? [],
          agentMessages: record.progress?.messages ?? {},
          personas: personaCardsFromProgress(record.progress?.personas),
          intake: {
            name: record.companyName || draft.name || '',
            url: record.websiteUrl || draft.url || '',
            type: record.businessType || draft.type || '',
            desc: draft.desc || '',
            specialties: draft.specialties || '',
            location: draft.location || '',
            ...(draft.companyTypeId ? { companyTypeId: draft.companyTypeId } : {}),
          },
        });
      })
      .catch(() => settle({ status: 'none' }));

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
